///
/// EarthBrowser
///
/// Created by Matt Giger on 7/15/2024
/// Copyright (c) 2024 Geodata Labs
///

import { cn } from "@/components/utils/utils"
import { Button } from "@/components/ui/button"
import { CookieIcon } from "lucide-react"
import { useEffect, useState } from "react"
import Link from "@/components/utils/Link"
import { isNativeApp } from "@/native"
import { useAppSelector } from "@/redux/store"
import { userApi } from "@/redux/userApi"
import { updatePerformanceConsent } from "@/utils/privacy-consent"

const cConsentShowDelay = 10000

export function CookieConsent() {
	// The web cookie banner doesn't belong in the store apps. With no choice
	// recorded, initializeTrackingConsent() keeps PostHog/Sentry off, and the
	// profile's privacy settings remain the place to opt in.
	if (isNativeApp()) {
		return null
	}
	return <CookieConsentBanner />
}

function CookieConsentBanner() {
	const [isOpen, setIsOpen] = useState(false)
	const [hide, setHide] = useState(false)
	const userProfile = useAppSelector((state) => state.user.userProfile)
	const [updatePrivacySettings] = userApi.useUpdatePrivacySettingsMutation()

	useEffect(() => {
		const timeoutId = setTimeout(() => {
			setIsOpen(true)
			if (document.cookie.includes("cookieConsent=true")) {
				setIsOpen(false)
				setTimeout(() => { setHide(true) }, 700)
			}
		}, cConsentShowDelay)

		return () => clearTimeout(timeoutId)
	}, [])

	const buttonClicked = async (accept: boolean) => {
		setIsOpen(false)

		// Always set the cookie for immediate effect
		document.cookie = `cookieConsent=${accept ? "true" : "false"}; expires=Fri, 31 Dec 9999 23:59:59 GMT`

		// Update tracking consent (PostHog and Sentry)
		updatePerformanceConsent(accept)

		// If user is authenticated, also save to database
		if (userProfile !== undefined) {
			try {
				await updatePrivacySettings({
					performance_cookies: accept,
					personalization_cookies: accept,
				}).unwrap()
			} catch (error) {
				console.error("Failed to update privacy settings in database:", error)
			}
		}
	}

	return (
		<div className={cn("fixed z-200 bottom-0 left-0 right-0 sm:left-4 sm:bottom-4 w-full sm:max-w-sm transition-all duration-700", !isOpen ? "translate-y-8 opacity-0 pointer-events-none" : "translate-y-0 opacity-100", hide && "hidden")}>
			<div className="m-3 bg-background/50 backdrop-blur-xl border border-border/50 rounded-2xl shadow-xl overflow-hidden">
				<div className="p-4 space-y-3">
					{/* Header with icon */}
					<div className="flex items-center gap-2">
						<CookieIcon className="h-4 w-4 text-blue-600" />
						<h2 className="text-sm font-bold text-foreground">Cookie Settings</h2>
					</div>

					{/* Content */}
					<p className="text-xs text-foreground/80 leading-relaxed">
						We use cookies to enhance your experience. By clicking &quot;Accept&quot;, you agree to our use of cookies.{" "}
						<Link href="/cookies" className="text-blue-600 dark:text-blue-400 underline hover:text-blue-700">Learn more</Link>
					</p>

					{/* Buttons */}
					<div className="flex gap-2">
						<Button
							onClick={() => buttonClicked(false)}
							variant="outline"
							size="sm"
							className="flex-1 rounded-full border-blue-500/50 hover:bg-blue-500/10 hover:border-blue-500 text-xs"
						>
							Decline
						</Button>
						<Button
							onClick={() => buttonClicked(true)}
							size="sm"
							className="flex-1 rounded-full shadow-lg transition-all duration-300 hover:scale-105 hover:shadow-xl bg-gradient-to-r from-blue-600 to-cyan-600 hover:from-blue-700 hover:to-cyan-700 text-white text-xs font-medium"
						>
							Accept
						</Button>
					</div>
				</div>
			</div>
		</div>
	)
}